import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from "react";

export type OpenExtractionReviewOpts = {
  /** When set, open Sheet on Step B for this extraction. */
  extractionId?: string;
};

type ExtractionReviewContextValue = {
  isOpen: boolean;
  focusedExtractionId: string | null;
  openReview: (opts?: OpenExtractionReviewOpts) => void;
  closeReview: () => void;
  /** Return to Step A (inbox) without closing the Sheet. */
  backToInbox: () => void;
};

const ExtractionReviewContext = createContext<ExtractionReviewContextValue | null>(null);

export function useExtractionReview(): ExtractionReviewContextValue {
  const ctx = useContext(ExtractionReviewContext);
  if (!ctx) {
    throw new Error("useExtractionReview must be used within ExtractionReviewProvider");
  }
  return ctx;
}

/** Soft open — no-ops when provider is absent (e.g. isolated tests). */
export function useOptionalExtractionReview(): ExtractionReviewContextValue | null {
  return useContext(ExtractionReviewContext);
}

export function ExtractionReviewProvider({ children }: { children: ReactNode }) {
  const [isOpen, setIsOpen] = useState(false);
  const [focusedExtractionId, setFocusedExtractionId] = useState<string | null>(null);

  const openReview = useCallback((opts?: OpenExtractionReviewOpts) => {
    setFocusedExtractionId(opts?.extractionId ?? null);
    setIsOpen(true);
  }, []);

  const closeReview = useCallback(() => {
    setIsOpen(false);
    setFocusedExtractionId(null);
  }, []);

  const backToInbox = useCallback(() => {
    setFocusedExtractionId(null);
  }, []);

  const value = useMemo(
    () => ({
      isOpen,
      focusedExtractionId,
      openReview,
      closeReview,
      backToInbox,
    }),
    [isOpen, focusedExtractionId, openReview, closeReview, backToInbox],
  );

  return (
    <ExtractionReviewContext.Provider value={value}>{children}</ExtractionReviewContext.Provider>
  );
}
